import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Snackbar, Alert, AlertColor,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

type ToastSeverity = AlertColor;

interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
}

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: 'info' | 'warning' | 'error';
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  resolve?: (value: boolean) => void;
}

interface ToastContextValue {
  show: (message: string, severity?: ToastSeverity) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
  confirm: (opts: ConfirmOptions | string) => Promise<boolean>;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
  const [confirmState, setConfirmState] = useState<ConfirmState>({ open: false, message: '' });

  const show = useCallback((message: string, severity: ToastSeverity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const success = useCallback((m: string) => show(m, 'success'), [show]);
  const error = useCallback((m: string) => show(m, 'error'), [show]);
  const info = useCallback((m: string) => show(m, 'info'), [show]);
  const warning = useCallback((m: string) => show(m, 'warning'), [show]);

  const confirm = useCallback((opts: ConfirmOptions | string) => {
    const options: ConfirmOptions = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise<boolean>((resolve) => {
      setConfirmState({ ...options, open: true, resolve });
    });
  }, []);

  const closeConfirm = (result: boolean) => {
    confirmState.resolve?.(result);
    setConfirmState((s) => ({ ...s, open: false, resolve: undefined }));
  };

  const closeToast = () => setToast((s) => ({ ...s, open: false }));

  return (
    <ToastContext.Provider value={{ show, success, error, info, warning, confirm }}>
      {children}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={closeToast} variant="filled" sx={{ minWidth: 280 }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmState.open} onClose={() => closeConfirm(false)} maxWidth="xs" fullWidth>
        {confirmState.title && <DialogTitle>{confirmState.title}</DialogTitle>}
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{confirmState.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeConfirm(false)}>
            {confirmState.cancelText || t('common.cancel')}
          </Button>
          <Button
            onClick={() => closeConfirm(true)}
            variant="contained"
            color={confirmState.severity === 'error' ? 'error' : 'primary'}
            autoFocus
          >
            {confirmState.confirmText || t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ToastContext.Provider>
  );
};
